import { roundTo2Decimals, isNumber } from '../../../utils/index.js';
import { transformProductsAttributeOption } from '../../attributes/transformers.js';
import { transformBundleOptionsRequest } from '../bundleOptions/index.js';
import { transformConfigurableOptionsRequest } from '../configurableOptions/index.js';
import { transformCustomOptionsRequest } from '../customOptions/index.js';
import { transformGiftCardOptionsRequest } from '../giftcardOptions/index.js';
import { transformGroupedOptionsRequest } from '../groupedOptions/index.js';

const sortingGiftcardOptions = [
    'senderName',
    'senderEmail',
    'recipientName',
    'recipientEmail',
    'message'
];
const transformCartWishlistItemsRequest = ({ sku , qty , bundleOptions , customOptions , configurableOptions , groupedOptions , giftcardOptions  })=>{
    return {
        sku,
        qty: parseInt(qty, 10),
        metadata: {
            ...bundleOptions && bundleOptions?.length > 0 ? {
                bundle_options: transformBundleOptionsRequest(bundleOptions)
            } : {},
            ...configurableOptions && configurableOptions?.length > 0 ? {
                configurable_options: transformConfigurableOptionsRequest(configurableOptions)
            } : {},
            ...customOptions && customOptions?.length > 0 ? {
                custom_options: transformCustomOptionsRequest(customOptions)
            } : {},
            ...groupedOptions && groupedOptions?.length > 0 ? {
                grouped_options: transformGroupedOptionsRequest(groupedOptions)
            } : {},
            ...giftcardOptions ? {
                giftcard_options: transformGiftCardOptionsRequest(giftcardOptions)
            } : {}
        }
    };
};
/**
 * This method is made by Tony's indications
 */ const transformProductOptions = (productOptions = [], productData, metadata)=>{
    const isConfigurable = productData?.type === 'configurable';
    const transformedProductOptions = productOptions && productOptions?.length ? productOptions?.map(({ title , children  })=>{
        const isSingleOption = children.every(({ value  })=>!!value);
        const theSingleOption = children[0];
        return {
            label: title,
            type: productData.type,
            value: isSingleOption ? [
                {
                    content: theSingleOption?.value,
                    sku: theSingleOption?.sku,
                    qty: isConfigurable ? null : theSingleOption?.qty
                }
            ] : children.map(({ product_id , sku , name , qty , assets  })=>({
                    id: product_id,
                    sku,
                    content: name,
                    qty,
                    assets: transformProductAssets(assets)
                }))
        };
    }) : [];
    const giftcardProductOptions = Object.entries(metadata.giftcardOptions ?? {})?.filter(([id, value])=>![
            'amount',
            'openAmount'
        ].includes(id) && !!value)?.sort(([a], [b])=>sortingGiftcardOptions.indexOf(a) - sortingGiftcardOptions.indexOf(b))?.map(([id, value])=>{
        let translationId;
        switch(id){
            case 'recipientName':
                translationId = 'giftcard_recipient_name';
                break;
            case 'recipientEmail':
                translationId = 'giftcard_recipient_email';
                break;
            case 'senderName':
                translationId = 'giftcard_sender_name';
                break;
            case 'senderEmail':
                translationId = 'giftcard_sender_email';
                break;
            case 'message':
                translationId = 'giftcard_message';
                break;
            default:
                translationId = id;
        }
        return {
            type: 'giftcard',
            translationId,
            value: [
                {
                    id,
                    content: value
                }
            ]
        };
    });
    const customProductOptions = metadata.customOptions?.map(({ id , value  })=>{
        const customOption = productData.customOptions.find((customOptions)=>customOptions.id === id);
        if (!customOption) {
            return false;
        }
        let optionValue;
        switch(customOption.type){
            case 'field':
            case 'area':
                optionValue = [
                    {
                        id,
                        content: value,
                        price: customOption.price,
                        sku: customOption.sku
                    }
                ];
                break;
            case 'drop_down':
            case 'radio':
                optionValue = [
                    customOption.values.find(({ id  })=>value === id)
                ];
                break;
            case 'checkbox':
            case 'multiple':
                optionValue = customOption.values.filter(({ id  })=>value.toString().split(',').map((n)=>parseInt(n)).includes(id));
                break;
            case 'date':
            case 'time':
            case 'date_time':
            case 'file':
            default:
                optionValue = [
                    {
                        id,
                        content: value,
                        price: customOption.price,
                        sku: customOption.sku
                    }
                ];
        }
        return {
            type: 'custom',
            label: customOption.label,
            value: optionValue,
            code: customOption.code
        };
    }).filter((option)=>!!option);
    const extraProductOptions = [
        ...customProductOptions,
        ...giftcardProductOptions
    ];
    return [
        ...transformedProductOptions,
        ...extraProductOptions
    ];
};
const transformConfigurableOptionsToProductOptionsForOutOfStockSubscriptions = (product, parentProduct = {})=>{
    const { configurableOptions , type  } = parentProduct;
    return configurableOptions?.map(({ attributeCode , label  })=>{
        return {
            type,
            label,
            value: [
                {
                    id: product.productData.attributes[attributeCode].id,
                    content: product.productData.attributes[attributeCode].value
                }
            ]
        };
    });
};
const transformProductAttributes = (attributes)=>{
    if (!attributes) {
        return {};
    }
    const transformedAttributes = Object.keys(attributes).map((key)=>({
            [key]: {
                ...transformProductsAttributeOption({
                    label: attributes[key].title,
                    key: attributes[key].key,
                    swatch: attributes[key]?.swatch
                }),
                value: attributes[key].value === '0' ? false : attributes[key].value === '1' ? true : attributes[key].value === ' ' || attributes[key].value === '' ? null : attributes[key].value
            }
        })).reduce((a, b)=>Object.assign(a, b), {});
    // Remove giftcard attributes
    delete transformedAttributes.giftcard_amounts;
    delete transformedAttributes.allow_open_amount;
    delete transformedAttributes.open_amount_min;
    delete transformedAttributes.open_amount_max;
    delete transformedAttributes.allow_message;
    // Remove duplicate attributes
    delete transformedAttributes.name;
    delete transformedAttributes.sku;
    delete transformedAttributes.description;
    delete transformedAttributes.short_description;
    delete transformedAttributes.url_key;
    // Remove template
    delete transformedAttributes.refilo_template;
    // Remove is_preorder
    delete transformedAttributes.is_preorder;
    return transformedAttributes;
};
const transformOptionValues = ({ code , value , label , value_index , swatch , tot  })=>({
        defaultLabel: code || '',
        ...value_index ? {
            value: parseInt(value_index)
        } : {},
        label: swatch.type === 'textual' ? swatch.value : label ? label : value,
        type: swatch.type === 'swatch' ? 'image' : swatch.type === 'color' ? 'hex' : 'text',
        ...swatch.type === 'swatch' ? {
            src: swatch.value
        } : {},
        ...swatch.type === 'color' ? {
            hex: swatch.value
        } : {},
        ...swatch?.value_hex ? {
            additionalHex: swatch?.value_hex
        } : {},
        ...swatch?.value_bg ? {
            additionalBg: swatch?.value_bg
        } : {},
        count: tot || null
    });
function transformProductAssets(assets) {
    return Object.keys(assets?.images || {}).map((key)=>({
            type: assets.images[key].media_type === 'external-video' ? 'video' : assets.images[key].media_type,
            label: assets.images[key].label,
            alt: assets.images[key].alt,
            image: assets.images[key].file,
            video: assets.images[key]?.video_url?.replace(/^\/media\//g, '') ?? null,
            videoTitle: assets.images[key].video_title,
            videoDescription: assets.images[key].video_description,
            videoMetadata: assets.images[key].video_metadata,
            videoProvider: assets.images[key].video_provider,
            roles: Object.keys(assets.placeholders).filter((role)=>assets.placeholders[role] === assets.images[key].file),
            hideFromGallery: assets.images[key].hide_from_gallery ?? false
        }));
}
const transformProductPrices = ({ type , attributes , pricing , middle_price , hide_price , old_price  })=>{
    let minPrice = pricing?.min_price || null;
    let maxPrice = pricing?.max_price || null;
    let basePrice = pricing?.base_price || null;
    let finalPrice = pricing?.final_price || null;
    let middlePrice = middle_price || pricing?.base_price || null;
    let oldPrice = old_price || pricing?.base_price || null;
    if (type === 'giftcard') {
        const giftCardAmounts = attributes?.giftcard_amounts?.value?.map(({ value  })=>roundTo2Decimals(parseFloat(value))) ?? [];
        const openAmount = {
            allowed: attributes?.allow_open_amount?.value === 'Yes',
            min: roundTo2Decimals(parseFloat(attributes?.open_amount_min?.value)),
            max: roundTo2Decimals(parseFloat(attributes?.open_amount_max?.value))
        };
        const minAmountOfAmounts = Math.min(...giftCardAmounts);
        const minAmountOfOpenAmount = openAmount.allowed ? openAmount.min : null;
        const maxAmountOfAmounts = Math.max(...giftCardAmounts);
        const maxAmountOfOpenAmount = openAmount.allowed ? openAmount.max : null;
        minPrice = Math.min(...[
            minAmountOfAmounts,
            minAmountOfOpenAmount
        ].filter(isNumber));
        maxPrice = Math.max(...[
            maxAmountOfAmounts,
            maxAmountOfOpenAmount
        ].filter(isNumber));
        basePrice = minPrice;
        finalPrice = basePrice;
        middlePrice = basePrice;
        oldPrice = basePrice;
    }
    return {
        base: basePrice,
        middle: middlePrice,
        final: finalPrice,
        max: maxPrice,
        min: minPrice,
        hide: hide_price ?? false,
        old: oldPrice
    };
};
const transformBundleItems = (bundle)=>{
    const items = bundle?.map(({ id , default_title , title , required , type , childs  })=>({
            id,
            // fallback on translated label if code is not set
            code: default_title ?? title,
            label: title,
            required: required === 1,
            type,
            children: childs.map(({ id , product_id , product_sku , qty , is_default , can_change_quantity , is_salable  })=>({
                    id,
                    productId: product_id,
                    productSku: product_sku,
                    defaultQty: qty,
                    isDefault: is_default === 1,
                    canChangeQty: can_change_quantity === 1,
                    saleable: is_salable ?? true
                }))
        })) || [];
    items.forEach(({ required , children  })=>{
        if (required) {
            // a default options always need to be present
            // to be able to add the product to the cart
            // if no default option is present, set the first one as default
            if (children?.length > 0 && !children.some(({ isDefault  })=>isDefault)) {
                children[0].isDefault = true;
            }
        }
    });
    return items;
};
const transformCatalogProductResponse = ({ id , sku , name , type , childs , variants , bundle , bundle_price_type , assets , attributes , categories , pricing , inventory , meta_title , meta_keywords , meta_description , custom_options , rating , hide_price , is_salable , middle_price , max_qty , min_qty , step , old_price , inSearchContext =false , __bhSearchId =null , __bhSearchIdentifiedAttributes =null , ...rest } = {})=>({
        id: id || null,
        sku: sku ?? null,
        name: name || null,
        description: attributes?.description?.value || null,
        shortDescription: attributes?.short_description?.value || null,
        urlKey: attributes?.url_key?.value || null,
        layout: {
            template: attributes?.refilo_template?.value || null
        },
        type: type || null,
        childrenIds: childs || [],
        configurableOptions: variants?.map(({ type , label , attribute_id , attribute_code , rendered , values , update_product_media  })=>({
                type,
                label,
                attributeId: parseInt(attribute_id),
                attributeCode: attribute_code,
                renderer: rendered || 'select',
                valuesHaveDifferentMedia: update_product_media === '1',
                values: values.map(transformOptionValues)
            })).sort((a, b)=>parseInt(a.position) < parseInt(b.position) ? -1 : 0) || [],
        giftcardOptions: attributes?.giftcard_amounts?.value ? {
            type: attributes?.giftcard_type?.value,
            amounts: attributes?.giftcard_amounts?.value?.map(({ value_id , value  })=>({
                    value: parseInt(value_id),
                    price: roundTo2Decimals(parseFloat(value))
                })) || [],
            openAmount: {
                allowed: attributes?.allow_open_amount?.value === 'Yes',
                min: roundTo2Decimals(parseFloat(attributes?.open_amount_min?.value)),
                max: roundTo2Decimals(parseFloat(attributes?.open_amount_max?.value))
            },
            allowMessage: attributes?.allow_message?.value === '1'
        } : null,
        bundleOptions: bundle_price_type && bundle?.length ? {
            priceType: bundle_price_type || null,
            items: transformBundleItems(bundle)
        } : null,
        customOptions: custom_options?.map(({ option_id , type , is_require , sku , max_characters , default_title , title , price , values , file_extension , image_size_x , image_size_y  })=>{
            return {
                id: parseInt(option_id),
                type,
                required: is_require === '1',
                sku,
                maxCharacters: max_characters !== '0' ? parseInt(max_characters) : null,
                code: default_title,
                label: title,
                price: !isNumber(price) ? null : roundTo2Decimals(parseFloat(price)),
                fileExtensions: file_extension ? file_extension.split(', ') : null,
                imageSizeX: parseInt(image_size_x),
                imageSizeY: parseInt(image_size_y),
                values: values.map(({ option_type_id , sku , default_title , title , price  })=>{
                    return {
                        id: parseInt(option_type_id),
                        sku,
                        code: default_title,
                        label: title,
                        price: !isNumber(price) ? null : roundTo2Decimals(parseFloat(price))
                    };
                }).sort((a, b)=>parseInt(a.sort_order) < parseInt(b.sort_order) ? -1 : 0)
            };
        }).sort((a, b)=>parseInt(a.sort_order) < parseInt(b.sort_order) ? -1 : 0) || [],
        assets: transformProductAssets(assets),
        attributes: transformProductAttributes(attributes),
        categoryIds: categories || [],
        prices: transformProductPrices({
            type,
            attributes,
            pricing,
            middle_price,
            hide_price,
            old_price
        }),
        inventory: {
            inStock: inventory?.in_stock || false,
            saleable: is_salable ?? inventory?.saleable ?? false,
            allowBackOrders: !((inventory?.backorders || 0) === 0) || parseInt(attributes?.is_preorder?.value || 0) === 1,
            minQty: min_qty ?? 1,
            maxQty: max_qty ?? 10,
            step: step ?? 1
        },
        seoMeta: {
            title: meta_title || '',
            description: meta_description || '',
            keywords: meta_keywords || '',
            robots: 'index,follow'
        },
        ogData: {
            title: meta_title || name || '',
            description: meta_description || attributes?.description?.value || '',
            image: assets?.placeholders?.image || '',
            url: ''
        },
        rating: {
            summary: rating?.global?.review_summary || null,
            count: rating?.global?.review_count || null
        },
        inSearchContext,
        __bhSearchId,
        __bhSearchIdentifiedAttributes,
        __rest: rest
    });

export { transformCartWishlistItemsRequest, transformCatalogProductResponse, transformConfigurableOptionsToProductOptionsForOutOfStockSubscriptions, transformOptionValues, transformProductAssets, transformProductAttributes, transformProductOptions, transformProductPrices };
